// Admin portal shell. CONTEXT.md D-09: react-router-dom v5.3.4 + Bootstrap 5 + reactstrap.
// Server-side `[Authorize(Policy = "GlobalAdmin")]` is the real security gate (Program.cs).
// Bootstrap container with a vertical pill nav on the left and a route-driven content
// slot on the right. New admin pages plug into additional NavItem + Route entries here.
//
// Mounted as the SPA root on the admin.<host> subdomain (App.js), so match.url/match.path
// is "/" — the helper below normalizes that to "" so child paths come out as "/sites"
// rather than "//sites". The component is reusable: if a future host mounts it under
// "/admin", match.url/match.path = "/admin" and the same code yields "/admin/sites".
//
// Phase 999.4 Plan 05 (R1): Sites is the new default landing surface — Userlog Viewer
// is demoted from default but remains accessible at /userlog. Sites NavItem appears
// ABOVE Userlog Viewer per Discretion item 7. Routes for /sites/create and
// /sites/:locationId/config are added in Plans 06/07 alongside their components.

import React from 'react';
import { Switch, Route, NavLink, Redirect } from 'react-router-dom';
import { Container, Row, Col, Nav, NavItem } from 'reactstrap';
import { AdminGuard } from './AdminGuard';
import { UserLogViewer } from './UserLogViewer';
import { SitesTable } from './SitesTable';
import { SiteConfigView } from './SiteConfigView';
import { CreateSiteWizard } from './CreateSiteWizard';

const stripRoot = (s) => (s === '/' ? '' : s);

export function AdminLayout({ auth, match }) {
    const baseUrl = stripRoot(match.url);
    const basePath = stripRoot(match.path);
    return (
        <AdminGuard auth={auth}>
            <Container fluid className="mt-3">
                <Row>
                    <Col xs={12} md={3} lg={2} className="border-end pe-3">
                        <h4 className="mb-3">Admin</h4>
                        <Nav vertical pills>
                            <NavItem>
                                {/* react-router-dom v5 NavLink: activeClassName is a STRING, not a function. */}
                                <NavLink
                                    to={`${baseUrl}/sites`}
                                    className="nav-link"
                                    activeClassName="active"
                                    exact
                                >
                                    Sites
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                {/* Phase 999.4 Plan 07 — direct entry to CreateSiteWizard. Routed
                                    BEFORE /sites/:locationId(\d+)/config in the Switch below so
                                    the literal "create" segment never matches the numeric regex. */}
                                <NavLink
                                    to={`${baseUrl}/sites/create`}
                                    className="nav-link"
                                    activeClassName="active"
                                >
                                    Create Site
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    to={`${baseUrl}/userlog`}
                                    className="nav-link"
                                    activeClassName="active"
                                >
                                    Userlog Viewer
                                </NavLink>
                            </NavItem>
                            {/* Future admin pages plug into additional NavItem entries here. */}
                        </Nav>
                    </Col>
                    <Col xs={12} md={9} lg={10}>
                        <Switch>
                            {/* Phase 999.4 Plan 07: literal /sites/create route placed FIRST so the
                                Switch (which matches in order) never falls through to the numeric
                                config route. Plan 06's /sites/:locationId(\d+)/config follows; the
                                regex constraint already disambiguates "create" from "12345" but
                                ordering creates a defence-in-depth layer for future maintainers
                                who might drop the constraint. */}
                            <Route
                                path={`${basePath}/sites/create`}
                                render={() => <CreateSiteWizard auth={auth} />}
                            />
                            <Route
                                path={`${basePath}/sites/:locationId(\\d+)/config`}
                                render={({ match: m }) => (
                                    <SiteConfigView
                                        locationId={parseInt(m.params.locationId, 10)}
                                        auth={auth}
                                    />
                                )}
                            />
                            <Route path={`${basePath}/sites`} exact render={() => <SitesTable auth={auth} />} />
                            <Route path={`${basePath}/userlog`} render={() => <UserLogViewer auth={auth} />} />
                            <Redirect from={match.path} to={`${baseUrl}/sites`} />
                        </Switch>
                    </Col>
                </Row>
            </Container>
        </AdminGuard>
    );
}

export default AdminLayout;
